import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Trophy, Star, Users, Brain, RefreshCw, Loader2, ChevronDown, ChevronUp,
    Target, Zap, Shield, ArrowRight, User, AlertTriangle, Sparkles, CheckCircle, AlertCircle
} from 'lucide-react';

const ROLE_ICON_MAP = {
    'Batsman': '🏏',
    'Bowler': '🎯',
    'All-rounder': '⚡',
    'Wicketkeeper': '🧤',
};

const ROLE_COLOR = {
    'Batsman': 'text-green-400 bg-green-500/10 border-green-500/20',
    'Bowler': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    'All-rounder': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    'Wicketkeeper': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

function PlayerCard({ player, index }) {
    const roleStyle = ROLE_COLOR[player.role] || 'text-white/40 bg-white/5 border-white/10';
    const isCaptain = player.is_captain_pick;
    const isVC = player.is_vc_pick;

    return (
        <div className={`relative bg-white/[0.03] rounded-xl border transition-all hover:bg-white/[0.06] ${
            isCaptain ? 'border-yellow-500/40 ring-1 ring-yellow-500/20' :
            isVC ? 'border-cyan-500/30 ring-1 ring-cyan-500/15' :
            'border-white/5'
        }`} data-testid={`fantasy-player-${index}`}>
            {/* Captain/VC Badge */}
            {(isCaptain || isVC) && (
                <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[9px] font-bold ${
                    isCaptain ? 'bg-yellow-500 text-black' : 'bg-cyan-500 text-black'
                }`}>
                    {isCaptain ? 'C' : 'VC'} {player.multiplier}
                </div>
            )}

            <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm"
                            style={{ backgroundColor: `${player.team_color}20`, color: player.team_color }}>
                            {index + 1}
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-white">{player.name}</div>
                            <div className="text-[10px] text-white/30">{player.team_short}</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-base font-bold text-cyan-400">{player.fantasy_score}</div>
                        <div className="text-[9px] text-white/20 tech-mono">FPTS</div>
                    </div>
                </div>

                <div className="flex items-center justify-between mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${roleStyle}`}>
                        {player.role}
                    </span>
                    <div className="flex items-center gap-3 text-[10px] text-white/30">
                        {player.role !== 'Bowler' && (
                            <span>AVG {player.batting_avg || 0}</span>
                        )}
                        {player.role !== 'Bowler' && (
                            <span>SR {player.strike_rate || 0}</span>
                        )}
                        {player.role !== 'Batsman' && player.role !== 'Wicketkeeper' && (
                            <span>ECO {player.economy || 0}</span>
                        )}
                        <span className={`font-medium ${player.form >= 0.8 ? 'text-green-400' : player.form >= 0.6 ? 'text-amber-400' : 'text-red-400'}`}>
                            Form {(player.form * 10).toFixed(1)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function FantasyXI() {
    const [upcoming, setUpcoming] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [fantasy, setFantasy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [gptAnalysis, setGptAnalysis] = useState(null);
    const [analyzingGpt, setAnalyzingGpt] = useState(false);
    const [showBench, setShowBench] = useState(false);

    const fetchUpcoming = useCallback(async () => {
        try {
            const res = await API.get('/fantasy/upcoming/matches');
            setUpcoming(res.data.matches || []);
        } catch (err) { console.error(err); }
        setLoading(false);
    }, []);

    useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);

    const generateFantasy = async (matchId) => {
        setGenerating(true);
        setGptAnalysis(null);
        setSelectedMatch(matchId);
        try {
            const res = await API.get(`/fantasy/${matchId}`);
            setFantasy(res.data);
            toast.success('Fantasy XI generated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate Fantasy XI');
        }
        setGenerating(false);
    };

    const analyzeWithGPT = async () => {
        if (!fantasy) return;
        setAnalyzingGpt(true);
        try {
            const res = await API.post(`/fantasy/${fantasy.match_id}/gpt-analysis`, {
                fantasy_xi: fantasy.fantasy_xi,
                captain: fantasy.captain,
                match: fantasy.match,
            });
            setGptAnalysis(res.data.analysis);
            if (res.data.generated) toast.success('GPT-5.2 analysis complete');
            else toast.error(res.data.error || 'GPT analysis unavailable');
        } catch (err) {
            toast.error('GPT analysis failed');
        }
        setAnalyzingGpt(false);
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="glass-card p-12 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="fantasy-xi-page">
            {/* Header */}
            <div className="glass-card p-5 border-l-4 border-l-purple-500">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">FANTASY XI GENERATOR</h1>
                            <div className="text-xs text-white/30 mt-0.5">
                                AI-powered optimal fantasy team selection across both squads
                            </div>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={fetchUpcoming} className="text-white/30 hover:text-white">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>

            {/* Match Selector */}
            <div className="glass-card p-5 border border-white/5">
                <div className="flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4 text-cyan-400" />
                    <span className="heading-primary text-sm text-white">SELECT MATCH</span>
                    <span className="text-[10px] text-white/20 tech-mono">
                        {upcoming.filter(m => m.status !== 'completed').length} upcoming | {upcoming.filter(m => m.status === 'completed').length} completed
                    </span>
                </div>

                {/* Upcoming Matches */}
                {upcoming.filter(m => m.status !== 'completed').length > 0 && (
                    <div className="mb-3">
                        <div className="text-[10px] text-white/30 tech-mono mb-2">UPCOMING — GENERATE FANTASY XI</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {upcoming.filter(m => m.status !== 'completed').map(m => (
                                <button key={m.id}
                                    onClick={() => generateFantasy(m.id)}
                                    disabled={generating}
                                    className={`text-left p-4 rounded-xl border transition-all ${
                                        selectedMatch === m.id
                                            ? 'bg-purple-500/10 border-purple-500/30'
                                            : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05] hover:border-white/10'
                                    }`}
                                    data-testid={`match-select-${m.id}`}>
                                    <div className="text-sm font-bold text-white">
                                        {m.home?.short_name} vs {m.away?.short_name}
                                    </div>
                                    <div className="text-[10px] text-white/30 tech-mono mt-1">
                                        {m.date?.slice(0, 10)} | {m.venue?.city || m.venue?.name || 'TBD'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Completed Matches — Check Accuracy */}
                {upcoming.filter(m => m.status === 'completed').length > 0 && (
                    <div>
                        <div className="text-[10px] text-white/30 tech-mono mb-2">COMPLETED — CHECK FANTASY ACCURACY</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {upcoming.filter(m => m.status === 'completed').map(m => (
                                <button key={m.id}
                                    onClick={() => generateFantasy(m.id)}
                                    disabled={generating}
                                    className={`text-left p-4 rounded-xl border transition-all ${
                                        selectedMatch === m.id
                                            ? 'bg-green-500/10 border-green-500/30'
                                            : 'bg-white/[0.02] border-green-500/10 hover:bg-white/[0.05] hover:border-green-500/20'
                                    }`}
                                    data-testid={`match-select-completed-${m.id}`}>
                                    <div className="text-sm font-bold text-white">
                                        {m.home?.short_name} vs {m.away?.short_name}
                                        <span className="ml-2 text-[10px] text-green-400 font-normal">COMPLETED</span>
                                    </div>
                                    <div className="text-[10px] text-white/30 tech-mono mt-1">
                                        {m.date?.slice(0, 10)} | {m.home_score} - {m.away_score}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {upcoming.length === 0 && (
                    <div className="text-xs text-white/20 text-center py-6">
                        No matches found in IPL 2026 schedule
                    </div>
                )}
                {generating && (
                    <div className="flex items-center gap-2 mt-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                        <span className="text-xs text-purple-300">Analyzing squads, venue conditions, form, and matchups...</span>
                    </div>
                )}
            </div>

            {/* Fantasy XI Result */}
            {fantasy && (
                <>
                    {/* Data Source & Toss Badge */}
                    {fantasy.data_source && (
                        <div className={`glass-card p-3 border ${
                            fantasy.source === 'frozen_prediction' ? 'border-blue-500/20 bg-blue-500/5' :
                            fantasy.has_toss ? 'border-green-500/20 bg-green-500/5' : 'border-yellow-500/20 bg-yellow-500/5'
                        }`}>
                            <div className="flex items-center gap-3" data-testid="fantasy-data-source">
                                {fantasy.source === 'frozen_prediction' ? (
                                    <Shield className="w-4 h-4 text-blue-400" />
                                ) : fantasy.has_toss ? (
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                    <AlertCircle className="w-4 h-4 text-yellow-400" />
                                )}
                                <div>
                                    <div className="text-xs font-bold text-white">
                                        {fantasy.source === 'frozen_prediction'
                                            ? 'FROZEN PRE-MATCH PREDICTION'
                                            : fantasy.data_source === 'playing_xi' ? 'BASED ON ACTUAL PLAYING XI' : 'BASED ON SQUAD PREDICTION'}
                                    </div>
                                    <div className="text-[10px] text-white/30">
                                        {fantasy.source === 'frozen_prediction'
                                            ? `Locked before match start${fantasy.frozen_at ? ` at ${new Date(fantasy.frozen_at).toLocaleString()}` : ''}. Comparing against actual performance.`
                                            : fantasy.has_toss
                                                ? `Toss: ${fantasy.toss_info?.winner || '?'} won, chose to ${fantasy.toss_info?.decision || '?'}`
                                                : 'Toss not yet happened — XI generated from predicted squad. Will update after toss.'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Accuracy Section (Completed Matches) */}
                    {fantasy.accuracy && (
                        <div className="glass-card p-5 border border-green-500/20" data-testid="fantasy-accuracy">
                            <div className="flex items-center gap-2 mb-3">
                                <Target className="w-4 h-4 text-green-400" />
                                <span className="heading-primary text-sm text-white">FANTASY XI ACCURACY</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                <div className="text-center bg-white/[0.03] rounded-lg p-3">
                                    <div className="text-[10px] text-white/30 tech-mono">SELECTION</div>
                                    <div className="text-xl font-bold text-cyan-400">{fantasy.accuracy.selection_accuracy}%</div>
                                    <div className="text-[10px] text-white/20">{fantasy.accuracy.players_in_match}/{fantasy.accuracy.total_xi} in match</div>
                                </div>
                                <div className="text-center bg-white/[0.03] rounded-lg p-3">
                                    <div className="text-[10px] text-white/30 tech-mono">POINTS ACC.</div>
                                    <div className="text-xl font-bold text-amber-400">{fantasy.accuracy.points_accuracy}%</div>
                                </div>
                                <div className="text-center bg-white/[0.03] rounded-lg p-3">
                                    <div className="text-[10px] text-white/30 tech-mono">PREDICTED PTS</div>
                                    <div className="text-xl font-bold text-white">{fantasy.accuracy.predicted_total}</div>
                                </div>
                                <div className="text-center bg-white/[0.03] rounded-lg p-3">
                                    <div className="text-[10px] text-white/30 tech-mono">ACTUAL PTS</div>
                                    <div className="text-xl font-bold text-white">{fantasy.accuracy.actual_total}</div>
                                </div>
                            </div>
                            {fantasy.accuracy.missed_stars?.length > 0 && (
                                <div>
                                    <div className="text-[10px] text-white/30 tech-mono mb-1">MISSED STARS (not in our XI but performed well)</div>
                                    <div className="flex gap-2">
                                        {fantasy.accuracy.missed_stars.map((s, i) => (
                                            <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5 text-xs">
                                                <span className="text-white font-bold">{s.name}</span>
                                                <span className="text-red-400 ml-1">{s.actual_points} pts</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Match Header */}
                    <div className="glass-card p-5 border border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-cyan-500/5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-lg font-bold text-white">
                                    {fantasy.match.home.short_name} vs {fantasy.match.away.short_name}
                                </div>
                                <div className="text-xs text-white/30">{fantasy.match.venue} | {fantasy.match.date?.slice(0, 10)}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button onClick={analyzeWithGPT} disabled={analyzingGpt}
                                    className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white text-xs"
                                    data-testid="gpt-analyze-btn">
                                    {analyzingGpt ? (
                                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analyzing...</>
                                    ) : (
                                        <><Brain className="w-3.5 h-3.5 mr-1.5" /> GPT-5.2 Analysis</>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-[10px] text-white/30 tech-mono">TOTAL SCORE</div>
                                <div className="text-xl font-bold text-cyan-400">{fantasy.total_fantasy_score}</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 text-center">
                                <div className="text-[10px] text-white/30 tech-mono">AVG FORM</div>
                                <div className={`text-lg font-bold ${fantasy.avg_form >= 0.75 ? 'text-green-400' : 'text-amber-400'}`}>
                                    {(fantasy.avg_form * 10).toFixed(1)}
                                </div>
                            </div>
                            {Object.entries(fantasy.team_split || {}).map(([team, count]) => (
                                <div key={team} className="bg-white/5 rounded-lg p-3 text-center">
                                    <div className="text-[10px] text-white/30 tech-mono">{team}</div>
                                    <div className="text-lg font-bold text-white">{count}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Captain & Vice Captain */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {fantasy.captain && (
                            <div className="glass-card p-4 border border-yellow-500/30 bg-gradient-to-r from-yellow-500/5 to-transparent"
                                data-testid="captain-pick">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-6 h-6 rounded-full bg-yellow-500 text-black flex items-center justify-center text-xs font-bold">C</div>
                                    <span className="text-[10px] text-yellow-400 tech-mono">CAPTAIN (2x POINTS)</span>
                                </div>
                                <div className="text-base font-bold text-white">{fantasy.captain.name}</div>
                                <div className="text-xs text-white/40">{fantasy.captain.team} | {fantasy.captain.role} | Score: {fantasy.captain.fantasy_score}</div>
                            </div>
                        )}
                        {fantasy.vice_captain && (
                            <div className="glass-card p-4 border border-cyan-500/30 bg-gradient-to-r from-cyan-500/5 to-transparent"
                                data-testid="vc-pick">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-6 h-6 rounded-full bg-cyan-500 text-black flex items-center justify-center text-xs font-bold">VC</div>
                                    <span className="text-[10px] text-cyan-400 tech-mono">VICE-CAPTAIN (1.5x POINTS)</span>
                                </div>
                                <div className="text-base font-bold text-white">{fantasy.vice_captain.name}</div>
                                <div className="text-xs text-white/40">{fantasy.vice_captain.team} | {fantasy.vice_captain.role} | Score: {fantasy.vice_captain.fantasy_score}</div>
                            </div>
                        )}
                    </div>

                    {/* Role Breakdown */}
                    <div className="glass-card p-4 border border-white/5">
                        <div className="text-[10px] text-white/30 tech-mono mb-3">ROLE BREAKDOWN</div>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(fantasy.role_breakdown || {}).map(([role, count]) => (
                                <div key={role} className={`px-3 py-1.5 rounded-lg border text-xs ${ROLE_COLOR[role] || 'text-white/40 bg-white/5 border-white/10'}`}>
                                    {ROLE_ICON_MAP[role] || ''} {count} {role}{count > 1 ? 's' : ''}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Fantasy XI Players */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 px-1">
                            <Users className="w-4 h-4 text-purple-400" />
                            <span className="heading-primary text-sm text-white">YOUR FANTASY XI</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {fantasy.fantasy_xi?.map((p, i) => (
                                <PlayerCard key={p.id || i} player={p} index={i} />
                            ))}
                        </div>
                    </div>

                    {/* Bench */}
                    {fantasy.bench?.length > 0 && (
                        <div className="glass-card border border-white/5 overflow-hidden">
                            <button onClick={() => setShowBench(!showBench)}
                                className="w-full p-4 flex items-center justify-between text-left"
                                data-testid="bench-toggle">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-white/20" />
                                    <span className="text-sm text-white/40">Bench / Next Best ({fantasy.bench.length})</span>
                                </div>
                                {showBench ? <ChevronUp className="w-4 h-4 text-white/20" /> : <ChevronDown className="w-4 h-4 text-white/20" />}
                            </button>
                            {showBench && (
                                <div className="px-4 pb-4 space-y-2">
                                    {fantasy.bench.map((p, i) => (
                                        <div key={p.id || i} className="flex items-center justify-between bg-white/[0.02] rounded-lg p-3 border border-white/5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-white/15 w-5">{i + 12}</span>
                                                <span className="text-xs text-white/50">{p.name}</span>
                                                <span className="text-[10px] text-white/20">({p.team_short})</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${ROLE_COLOR[p.role] || 'text-white/30 border-white/10'}`}>
                                                    {p.role}
                                                </span>
                                                <span className="text-xs text-white/30 tech-mono">{p.fantasy_score}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* GPT Analysis */}
                    {gptAnalysis && (
                        <div className="glass-card p-5 border border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-cyan-500/5"
                            data-testid="gpt-analysis-panel">
                            <div className="flex items-center gap-2 mb-4">
                                <Brain className="w-4 h-4 text-purple-400" />
                                <h2 className="heading-primary text-sm text-white">GPT-5.2 FANTASY ANALYSIS</h2>
                                <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 tech-mono">AI</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {gptAnalysis.rating && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-yellow-400 tech-mono mb-1">TEAM RATING</div>
                                        <div className="text-2xl font-bold text-yellow-400">{gptAnalysis.rating}<span className="text-sm text-white/20">/10</span></div>
                                    </div>
                                )}
                                {gptAnalysis.predicted_points && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-cyan-400 tech-mono mb-1">PREDICTED POINTS</div>
                                        <div className="text-lg font-bold text-cyan-400">{gptAnalysis.predicted_points}</div>
                                    </div>
                                )}
                                {gptAnalysis.captain_verdict && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-yellow-400 tech-mono mb-1">CAPTAIN VERDICT</div>
                                        <div className="text-xs text-white/60">{gptAnalysis.captain_verdict}</div>
                                    </div>
                                )}
                                {gptAnalysis.risk_assessment && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-amber-400 tech-mono mb-1">RISK ASSESSMENT</div>
                                        <div className="text-xs text-white/60">{gptAnalysis.risk_assessment}</div>
                                    </div>
                                )}
                                {gptAnalysis.differential_pick && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-green-400 tech-mono mb-1">DIFFERENTIAL PICK</div>
                                        <div className="text-xs text-white/60">{gptAnalysis.differential_pick}</div>
                                    </div>
                                )}
                                {gptAnalysis.transfer_suggestion && (
                                    <div className="bg-white/3 rounded-lg p-4">
                                        <div className="text-[10px] text-red-400 tech-mono mb-1">TRANSFER SUGGESTION</div>
                                        <div className="text-xs text-white/60">{gptAnalysis.transfer_suggestion}</div>
                                    </div>
                                )}
                            </div>
                            {gptAnalysis.verdict && (
                                <div className="mt-3 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg p-4 border border-purple-500/20">
                                    <div className="text-[10px] text-purple-400 tech-mono mb-1">VERDICT</div>
                                    <div className="text-sm text-white/80">{gptAnalysis.verdict}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Venue Analysis */}
                    {fantasy.venue_analysis && (
                        <div className="glass-card p-4 border border-white/5">
                            <div className="flex items-center gap-2 mb-3">
                                <Target className="w-3.5 h-3.5 text-white/20" />
                                <span className="text-[10px] text-white/20 tech-mono">VENUE FACTORS</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                                <div className="bg-white/[0.02] rounded-lg p-2">
                                    <div className="text-[9px] text-white/20">AVG 1st INNINGS</div>
                                    <div className="text-sm font-bold text-white/50">{fantasy.venue_analysis.avg_first_innings_score}</div>
                                </div>
                                <div className="bg-white/[0.02] rounded-lg p-2">
                                    <div className="text-[9px] text-white/20">CHASE WIN%</div>
                                    <div className="text-sm font-bold text-white/50">{fantasy.venue_analysis.chase_success_rate}%</div>
                                </div>
                                <div className="bg-white/[0.02] rounded-lg p-2">
                                    <div className="text-[9px] text-white/20">PACE ADV</div>
                                    <div className="text-sm font-bold text-white/50">{fantasy.venue_analysis.pace_advantage}%</div>
                                </div>
                                <div className="bg-white/[0.02] rounded-lg p-2">
                                    <div className="text-[9px] text-white/20">SPIN ADV</div>
                                    <div className="text-sm font-bold text-white/50">{fantasy.venue_analysis.spin_advantage}%</div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Rules Info */}
            <div className="glass-card p-4 border border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px] text-white/15">
                    <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-purple-400/30" />
                        11 players from both squads (min 4, max 7 per team)
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Zap className="w-3 h-3 text-yellow-400/30" />
                        Captain scores 2x, Vice-Captain scores 1.5x
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Brain className="w-3 h-3 text-cyan-400/30" />
                        AI factors: Form, venue, matchup, impact score
                    </div>
                </div>
            </div>
        </div>
    );
}
